import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  User,
  FileText,
  AlertTriangle,
  Users,
  Filter,
  Download,
  Mail,
  Eye,
  MoreHorizontal
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';
import { canApproveAbsences } from '../utils/roleUtils';

const AbsenceApprovalDashboard = ({ userRoles = [] }) => {
  const [pendingAbsences, setPendingAbsences] = useState([]);
  const [selectedAbsences, setSelectedAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    leave_type: 'all',
    department: '',
    date_range: 'all',
    priority: 'all'
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [batchApprovalNotes, setBatchApprovalNotes] = useState('');
  const [batchDenialReason, setBatchDenialReason] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // cards, table
  const [expandedAbsence, setExpandedAbsence] = useState(null);

  // Use centralized role utility to ensure super_admin is always included
  const canApprove = canApproveAbsences(userRoles);

  useEffect(() => {
    loadPendingAbsences();
  }, [filters, sortBy, sortOrder]);

  const loadPendingAbsences = async () => {
    setLoading(true);
    try {
      const absences = await apiService.getPendingAbsences();
      
      // Apply filters and sorting
      let filteredAbsences = absences.filter(absence => {
        if (filters.leave_type && filters.leave_type !== 'all' && absence.leave_type !== filters.leave_type) return false;
        if (filters.department && absence.department !== filters.department) return false;
        
        if (filters.date_range !== 'all') {
          const startDate = new Date(absence.start_date);
          const now = new Date();
          const daysDiff = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
          
          switch (filters.date_range) {
            case 'urgent': return daysDiff <= 2;
            case 'this_week': return daysDiff <= 7;
            case 'this_month': return daysDiff <= 30;
            default: return true;
          }
        }
        
        return true;
      });
      
      // Sort absences
      filteredAbsences.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        
        if (sortBy === 'created_at' || sortBy === 'start_date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
      
      setPendingAbsences(filteredAbsences);
    } catch (error) {
      console.error('Failed to load pending absences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending absences',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAbsenceSelection = (absenceId, selected) => {
    if (selected) {
      setSelectedAbsences(prev => [...prev, absenceId]);
    } else {
      setSelectedAbsences(prev => prev.filter(id => id !== absenceId));
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedAbsences(pendingAbsences.map(absence => absence.id));
    } else {
      setSelectedAbsences([]);
    }
  };

  const handleSingleApproval = async (absenceId, notes = '') => {
    setActionLoading(true);
    try {
      await apiService.updateAbsence(absenceId, {
        status: 'approved',
        approval_notes: notes
      });
      
      toast({
        title: 'Success',
        description: 'Absence request approved successfully'
      });
      
      await loadPendingAbsences();
    } catch (error) {
      console.error('Failed to approve absence:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve absence request',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingleDenial = async (absenceId, reason) => {
    if (!reason.trim()) {
      toast({
        title: 'Error',
        description: 'Denial reason is required',
        variant: 'destructive'
      });
      return;
    }
    
    setActionLoading(true);
    try {
      await apiService.updateAbsence(absenceId, {
        status: 'denied',
        approval_notes: reason
      });
      
      toast({
        title: 'Success',
        description: 'Absence request denied'
      });
      
      await loadPendingAbsences();
    } catch (error) {
      console.error('Failed to deny absence:', error);
      toast({
        title: 'Error',
        description: 'Failed to deny absence request',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBatchApproval = async () => {
    if (selectedAbsences.length === 0) return;
    
    setActionLoading(true);
    try {
      const result = await apiService.makeRequest('/api/absences/batch-approve', {
        method: 'POST',
        body: JSON.stringify({
          absence_ids: selectedAbsences,
          approval_notes: batchApprovalNotes
        })
      });
      
      toast({
        title: 'Success',
        description: `${result.approved_count} absence requests approved successfully`
      });
      
      setSelectedAbsences([]);
      setBatchApprovalNotes('');
      setShowBatchActions(false);
      await loadPendingAbsences();
      
    } catch (error) {
      console.error('Failed to batch approve:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve selected requests',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBatchDenial = async () => {
    if (selectedAbsences.length === 0 || !batchDenialReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please select requests and provide a denial reason',
        variant: 'destructive'
      });
      return;
    }
    
    setActionLoading(true);
    try {
      const result = await apiService.makeRequest('/api/absences/batch-deny', {
        method: 'POST',
        body: JSON.stringify({
          absence_ids: selectedAbsences,
          denial_reason: batchDenialReason
        })
      });
      
      toast({
        title: 'Success',
        description: `${result.denied_count} absence requests denied`
      });
      
      setSelectedAbsences([]);
      setBatchDenialReason('');
      setShowBatchActions(false);
      await loadPendingAbsences();
      
    } catch (error) {
      console.error('Failed to batch deny:', error);
      toast({
        title: 'Error',
        description: 'Failed to deny selected requests',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getPriorityLevel = (absence) => {
    const startDate = new Date(absence.start_date);
    const now = new Date();
    const daysDiff = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 1) return { level: 'urgent', color: 'bg-red-500', text: 'Urgent' };
    if (daysDiff <= 3) return { level: 'high', color: 'bg-orange-500', text: 'High' };
    if (daysDiff <= 7) return { level: 'medium', color: 'bg-yellow-500', text: 'Medium' };
    return { level: 'low', color: 'bg-green-500', text: 'Low' };
  };

  const getLeaveTypeColor = (leaveType) => {
    switch (leaveType) {
      case 'PTO': return 'bg-blue-100 text-blue-800';
      case 'SICK': return 'bg-red-100 text-red-800';
      case 'PERSONAL': return 'bg-purple-100 text-purple-800';
      case 'MEDICAL': return 'bg-orange-100 text-orange-800';
      case 'EMERGENCY': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const AbsenceCard = ({ absence }) => {
    const priority = getPriorityLevel(absence);
    const [showDetails, setShowDetails] = useState(false);
    const [approvalNotes, setApprovalNotes] = useState('');
    const [denialReason, setDenialReason] = useState('');
    
    return (
      <Card className="relative">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={selectedAbsences.includes(absence.id)}
                onCheckedChange={(checked) => handleAbsenceSelection(absence.id, checked)}
              />
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-medium">{absence.employee_name}</h3>
                  <Badge variant="secondary" className={getLeaveTypeColor(absence.leave_type)}>
                    {absence.leave_type}
                  </Badge>
                  <div className={`w-2 h-2 rounded-full ${priority.color}`} title={priority.text} />
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(absence.start_date)} - {formatDate(absence.end_date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{absence.total_hours}h</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mt-1">{absence.purpose}</p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        {showDetails && (
          <CardContent className="pt-0">
            <div className="space-y-3 border-t pt-3">
              {/* Additional Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Department:</span>
                  <span className="ml-2">{absence.department || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium">School:</span>
                  <span className="ml-2">{absence.school_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium">Submitted:</span>
                  <span className="ml-2">{formatDate(absence.created_at)}</span>
                </div>
                <div>
                  <span className="font-medium">Coverage:</span>
                  <span className="ml-2">
                    {absence.coverage_arranged ? (
                      <Badge variant="outline" className="text-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Arranged
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-orange-600">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Not Arranged
                      </Badge>
                    )}
                  </span>
                </div>
              </div>
              
              {absence.notes && (
                <div>
                  <span className="font-medium text-sm">Notes:</span>
                  <p className="text-sm text-gray-600 mt-1">{absence.notes}</p>
                </div>
              )}
              
              {/* Policy Violations */}
              {absence.policy_violations?.length > 0 && (
                <div>
                  <span className="font-medium text-sm text-orange-600">Policy Issues:</span>
                  <div className="mt-1 space-y-1">
                    {absence.policy_violations.map((violation, index) => (
                      <Alert key={index} variant="warning" className="py-2">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription className="text-sm">
                          {violation.message}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Coverage Employees */}
              {absence.coverage_employees?.length > 0 && (
                <div>
                  <span className="font-medium text-sm">Coverage Employees:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {absence.coverage_employees.map((emp, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {emp.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <Separator />
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    onClick={() => handleSingleApproval(absence.id, approvalNotes)}
                    disabled={actionLoading}
                    className="flex-1"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleSingleDenial(absence.id, denialReason)}
                    disabled={actionLoading || !denialReason.trim()}
                    className="flex-1"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Deny
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <Label htmlFor={`approval-notes-${absence.id}`} className="text-xs">
                      Approval Notes (optional)
                    </Label>
                    <Textarea
                      id={`approval-notes-${absence.id}`}
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      placeholder="Add any notes for approval..."
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`denial-reason-${absence.id}`} className="text-xs">
                      Denial Reason (required for denial)
                    </Label>
                    <Textarea
                      id={`denial-reason-${absence.id}`}
                      value={denialReason}
                      onChange={(e) => setDenialReason(e.target.value)}
                      placeholder="Provide reason for denial..."
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  if (!canApprove) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
          <p className="text-gray-600">
            You don't have permission to approve absence requests.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Absence Approvals</h2>
          <p className="text-gray-600">
            {pendingAbsences.length} pending request{pendingAbsences.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBatchActions(!showBatchActions)}
            disabled={selectedAbsences.length === 0}
          >
            <Users className="w-4 h-4 mr-1" />
            Batch Actions ({selectedAbsences.length})
          </Button>
          
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cards">Cards</SelectItem>
              <SelectItem value="table">Table</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select
              value={filters.leave_type || undefined}
              onValueChange={(value) => setFilters(prev => ({ ...prev, leave_type: value || '' }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Leave Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="PTO">PTO</SelectItem>
                <SelectItem value="SICK">Sick</SelectItem>
                <SelectItem value="PERSONAL">Personal</SelectItem>
                <SelectItem value="MEDICAL">Medical</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.date_range}
              onValueChange={(value) => setFilters(prev => ({ ...prev, date_range: value }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="urgent">Urgent (≤2 days)</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={sortBy}
              onValueChange={setSortBy}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Submitted</SelectItem>
                <SelectItem value="start_date">Start Date</SelectItem>
                <SelectItem value="employee_name">Employee</SelectItem>
                <SelectItem value="total_hours">Duration</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
            
            <div className="flex items-center space-x-2 ml-auto">
              <Checkbox
                checked={selectedAbsences.length === pendingAbsences.length && pendingAbsences.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm">Select All</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Batch Actions Panel */}
      {showBatchActions && selectedAbsences.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Batch Actions ({selectedAbsences.length} selected)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Batch Approval Notes (optional)</Label>
                <Textarea
                  value={batchApprovalNotes}
                  onChange={(e) => setBatchApprovalNotes(e.target.value)}
                  placeholder="Notes for all approved requests..."
                  rows={3}
                />
                <Button
                  onClick={handleBatchApproval}
                  disabled={actionLoading}
                  className="w-full"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve Selected ({selectedAbsences.length})
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label>Batch Denial Reason (required)</Label>
                <Textarea
                  value={batchDenialReason}
                  onChange={(e) => setBatchDenialReason(e.target.value)}
                  placeholder="Reason for denying all selected requests..."
                  rows={3}
                />
                <Button
                  variant="destructive"
                  onClick={handleBatchDenial}
                  disabled={actionLoading || !batchDenialReason.trim()}
                  className="w-full"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Deny Selected ({selectedAbsences.length})
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Absence List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading pending requests...</p>
        </div>
      ) : pendingAbsences.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Pending Requests</h3>
            <p className="text-gray-600">
              All absence requests have been processed.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingAbsences.map((absence) => (
            <AbsenceCard key={absence.id} absence={absence} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AbsenceApprovalDashboard;